import React, { useEffect, useRef, useState } from 'react';
import { PublicUser } from './api';
import { avatarStyle } from './AccountMenu';
import { Icon } from './icons';

/** A small call icon that opens video/audio options (and, in a channel, ring-specific-people). */
export function CallLauncher({
  kind,
  members,
  meId,
  onStart,
  onRing,
  className,
}: {
  kind: 'channel' | 'dm';
  members: PublicUser[];
  meId?: string;
  onStart: (video: boolean) => void;
  onRing: (memberIds: string[], video: boolean) => void;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [ringing, setRinging] = useState(false);
  const [picked, setPicked] = useState<Record<string, boolean>>({});
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) close();
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const others = members.filter((m) => m.id !== meId);
  const close = () => { setOpen(false); setRinging(false); setPicked({}); };
  // Starting a call rings the other participants so they actually get an
  // incoming-call alert with a Join button — a DM rings the partner, a channel
  // rings every member (product decision: a channel call is a summons, not an
  // announcement; the >150-member confirm guard upstream still applies).
  const start = (video: boolean) => {
    close();
    const ids = others.map((m) => m.id);
    if (ids.length) onRing(ids, video);
    else onStart(video);
  };
  const pickedIds = Object.keys(picked).filter((id) => picked[id]);
  const ring = (video: boolean) => {
    if (!pickedIds.length) return;
    close();
    onRing(pickedIds, video);
  };

  return (
    <div className={`fx-calllauncher ${className || ''}`} ref={ref}>
      <button
        type="button"
        className="fx-call-ic"
        title="Start a call"
        aria-label="Start a call"
        onClick={() => setOpen((v) => !v)}
      >
        <Icon name="phone" size={16} />
      </button>

      {open && (
        <div className="fx-call-pop">
          {!ringing ? (
            <>
              <button type="button" className="fx-call-opt" onClick={() => start(true)}>
                <Icon name="video" size={16} /> <span>Video call</span>
              </button>
              <button type="button" className="fx-call-opt" onClick={() => start(false)}>
                <Icon name="phone" size={16} /> <span>Audio call</span>
              </button>
              {kind === 'channel' && others.length > 0 && (
                <>
                  <div className="fx-call-sep" />
                  <button type="button" className="fx-call-opt" onClick={() => setRinging(true)}>
                    <Icon name="at" size={16} /> <span>Ring specific people…</span>
                  </button>
                </>
              )}
            </>
          ) : (
            <>
              <div className="fx-call-pop-title">Ring who?</div>
              <div className="fx-call-picklist">
                {others.map((m) => (
                  <label key={m.id} className="fx-call-pick">
                    <input
                      type="checkbox"
                      checked={!!picked[m.id]}
                      onChange={(e) => setPicked((p) => ({ ...p, [m.id]: e.target.checked }))}
                    />
                    <span className="fx-call-pick-av" style={avatarStyle(m)}>
                      {m.avatar_url ? '' : (m.name || m.email || '?')[0]?.toUpperCase()}
                      <span className={`fx-agent-dot${m.presence === 'away' ? ' is-away' : ''}`} />
                    </span>
                    <span className="fx-call-pick-name">{m.name || m.email}</span>
                  </label>
                ))}
              </div>
              <div className="fx-call-pop-foot">
                <button type="button" className="fx-btn" onClick={() => setRinging(false)}>Back</button>
                <div className="fx-call-pop-foot-actions">
                  <button type="button" className="fx-call-opt-btn" disabled={!pickedIds.length} onClick={() => ring(false)}>
                    <Icon name="phone" size={14} />
                  </button>
                  <button type="button" className="fx-call-opt-btn is-primary" disabled={!pickedIds.length} onClick={() => ring(true)}>
                    <Icon name="video" size={14} /> Ring {pickedIds.length || ''}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
